import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {UnderlinedButton} from '@/components/common/content/UnderlinedButton'
import {Link} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'

import {ArrowIcon} from '../icons/ArrowIcon'
import {ChevronIcon} from '../icons/ChevronIcon'

type BreadcrumbsProps =
  | {
      /*
       * The whole path, current page last. Rendering it rather than a single back-link is what
       * makes the BreadcrumbList emitted from the same steps honest, since structured data is meant
       * to describe a trail the reader can actually see.
       */
      trail: BreadcrumbStep[]
      // Ends the visible trail at the parent, for a page whose h1 already says the current step.
      hideCurrent?: boolean
    }
  // A single back-link to the parent page, for pages without a trail.
  | {label: string; linkHref: string}

export const Breadcrumbs: FC<BreadcrumbsProps> = (props) => {
  if (!('trail' in props)) {
    const {label, linkHref} = props

    return (
      <>
        <Link href={linkHref}>
          <span className="back-link">
            <ArrowIcon size={IconSize.small} color={theme.palette.text.light} variant="left" />
            <UnderlinedButton
              textVariant={TextVariant.text14}
              textColor={theme.palette.text.light}
              underlineColor={theme.palette.text.light}
              fontWeight={TextFontWeight.regular}
            >
              {label}
            </UnderlinedButton>
          </span>
        </Link>

        <style jsx>{`
          .back-link {
            display: inline-flex;
            align-items: center;
            gap: ${theme.spacing[2]};
          }
        `}</style>
      </>
    )
  }

  const {trail, hideCurrent = false} = props
  const current = trail[trail.length - 1]

  if (!current) {
    return null
  }

  const ancestors = trail.slice(0, -1)

  return (
    <>
      {/* A trail is navigation, and naming it lets a screen reader skip or announce it as one. */}
      <nav aria-label="Breadcrumb">
        <ol className="trail">
          {ancestors.map(({name, path}, index) => (
            <li key={path} className="step">
              <Link href={path}>
                <UnderlinedButton textVariant={TextVariant.text14}>{name}</UnderlinedButton>
              </Link>
              {(!hideCurrent || index < ancestors.length - 1) && (
                <span aria-hidden="true">
                  <ChevronIcon
                    size={IconSize.medium}
                    color={theme.palette.gray[400]}
                    variant="right"
                  />
                </span>
              )}
            </li>
          ))}
          {!hideCurrent && (
            <li className="step" aria-current="page">
              <Text variant={TextVariant.text14} color={theme.palette.text.light} element="span">
                {current.name}
              </Text>
            </li>
          )}
        </ol>
      </nav>

      <style jsx>{`
        .trail {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: ${theme.spacing[1.5]};
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .step {
          display: inline-flex;
          align-items: center;
          gap: ${theme.spacing[1.5]};
        }
      `}</style>
    </>
  )
}
